import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from '../api/auth';
import { configureAuth, setAccessToken } from '../api/client';

const REFRESH_TOKEN_KEY = 'rsms_refresh_token';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const clearTokens = useCallback(() => {
    setAccessToken(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }, []);

  const onSessionExpired = useCallback(() => {
    clearTokens();
    setUser(null);
    navigate('/login', { replace: true });
  }, [clearTokens, navigate]);

  useEffect(() => {
    configureAuth({
      getRefreshToken: () => {
        if (typeof window === 'undefined') {
          return null;
        }
        return window.localStorage.getItem(REFRESH_TOKEN_KEY);
      },
      setAccessToken,
      onSessionExpired
    });
  }, [onSessionExpired]);

  const initAuth = useCallback(async () => {
    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    const refreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      setIsLoading(false);
      return;
    }

    try {
      const tokenData = await authApi.refresh(refreshToken);
      setAccessToken(tokenData.access_token);
      if (tokenData.refresh_token) {
        window.localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token);
      }
      const meData = await authApi.me();
      setUser(meData);
    } catch (e) {
      clearTokens();
    } finally {
      setIsLoading(false);
    }
  }, [clearTokens]);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(
    async (email, password) => {
      setError(null);
      try {
        const tokenData = await authApi.login({ email, password });
        setAccessToken(tokenData.access_token);
        if (tokenData.refresh_token && typeof window !== 'undefined') {
          window.localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token);
        }
        const meData = await authApi.me();
        setUser(meData);
        navigate('/dashboard/projects', { replace: true });
        return null;
      } catch (err) {
        const msg = (err && err.message) || 'Login failed';
        setError(msg);
        return msg;
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // ignore API logout failures
    } finally {
      clearTokens();
      setUser(null);
      navigate('/login', { replace: true });
    }
  }, [clearTokens, navigate]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      login,
      logout,
      clearError
    }),
    [user, isLoading, error, login, logout, clearError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

